// src/App.js
import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { DashboardShell } from "./shared-ui/src/DashboardShell";
import "./shared-ui/src/styles.css";
import "./styles/App.css";

import { ThemeProvider } from "./theme/ThemeProvider";
import LoadingSpinner from "./components/LoadingSpinner";
import Breadcrumb from "./components/Breadcrumb";
import SummaryFilterSelect from "./components/SummaryFilterSelect";
import ToastContainer from "./components/ToastContainer";
import { useToast } from "./hooks/useToast";
import { fetchDevices, fetchBuildings, deleteDevice, recordDashboardLog } from "./services/api";
import { formatIotDashboardRoleLabel } from "./utils/iotDashboardRoles";
import { sortDevicesByNaturalName } from "./utils/naturalDeviceSort";

/** Product / UI events for Admin → session logs → “All dashboard activity” */
function logUiEvent(category, message, detail) {
  try {
    recordDashboardLog({
      level: "info",
      category,
      message,
      detail: detail != null ? String(detail) : undefined,
    });
  } catch {
    /* ignore */
  }
}

// Lazy load dashboard components for code splitting
const ThermostatDashboard = lazy(() => import("./pages/ThermostatDashboard"));
const WaterDashboard = lazy(() => import("./pages/WaterDashboard"));
const LeakDashboard = lazy(() => import("./pages/LeakDashboard"));
const AirDashboard = lazy(() => import("./pages/AirDashboard"));
const Tbhh100Dashboard = lazy(() => import("./pages/Tbhh100Dashboard"));
const EmberDashboard = lazy(() => import("./pages/EmberDashboard"));
const ControllerDashboard = lazy(() => import("./pages/ControllerDashboard"));
const DraginoDashboard = lazy(() => import("./pages/DraginoDashboardShell"));
const AlarmSetup = lazy(() => import("./pages/AlarmSetup"));
const LogsDashboard = lazy(() => import("./pages/LogsDashboard"));
const SensorSummaryHome = lazy(() => import("./pages/SensorSummaryHome"));
const SitingsPage = lazy(() => import("./pages/SitingsPage"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const SensorCategoriesPage = lazy(() => import("./pages/SensorCategoriesPage"));
const ThermostatGroupsPage = lazy(() => import("./pages/ThermostatGroupsPage"));
const OccupancyDashboard = lazy(() => import("./pages/OccupancyDashboard"));
const ControllerIoPage = lazy(() => import("./pages/ControllerIoPage"));
const AutomationsPage = lazy(() => import("./pages/AutomationsPage"));

function normalizeType(sensorType = "") {
  const s = String(sensorType).toLowerCase();
  if (s.includes("vs330") || s.includes("occupancy") || s.includes("bathroom")) return "vs330";
  if (s.includes("thermo")) return "thermostat";
  if (s.includes("saya") || (s.includes("water") && !s.includes("leak"))) return "water";
  if (s.includes("leak")) return "leak";
  if (s.includes("healthyhome") || s.includes("healthy_home") || s.includes("tbhh") || s.includes("temp_humidity"))
    return "tbhh100";
  if (s.includes("ember")) return "ember";
  if (s.includes("milesight_controller") || s.includes("milesight controller")) return "controller";
  if (s.includes("dragino")) return "dragino";
  if (s.includes("air") || s.includes("co2") || s.includes("iaq") || s.includes("environment") || s === "env")
    return "air";
  return "thermostat";
}

function readStoredDraginoCardRole() {
  try {
    const v = localStorage.getItem("selectedDraginoCardRole");
    return v === "controller" || v === "flow" ? v : null;
  } catch {
    return null;
  }
}

function writeStoredDraginoCardRole(role) {
  try {
    if (role === "controller" || role === "flow") {
      localStorage.setItem("selectedDraginoCardRole", role);
    } else {
      localStorage.removeItem("selectedDraginoCardRole");
    }
  } catch {
    /* ignore */
  }
}

function getDeviceBuildingId(device) {
  const raw = device?.buildingId ?? device?.buildingid ?? device?.building_id;
  return raw == null ? "" : String(raw).trim();
}

function normalizeBuildingKey(id) {
  return String(id || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "");
}

function buildingIdsMatch(a, b) {
  return normalizeBuildingKey(a) === normalizeBuildingKey(b);
}

function getBuildingRecordId(building) {
  const raw = building?.buildingId ?? building?.buildingid ?? building?.building_id;
  return raw == null ? "" : String(raw).trim();
}

function normalizeBuildingRecord(building) {
  return {
    ...building,
    buildingId: getBuildingRecordId(building),
  };
}

function readStoredShellDarkMode() {
  if (typeof window === "undefined") return false;
  try {
    const shellValue = window.localStorage.getItem("inAppDashboardDarkMode");
    if (shellValue === "true") return true;
    if (shellValue === "false") return false;

    const oldThemeValue = window.localStorage.getItem("fm_theme");
    if (oldThemeValue === "dark") return true;
    if (oldThemeValue === "light") return false;

    return Boolean(window.matchMedia?.("(prefers-color-scheme: dark)")?.matches);
  } catch {
    return false;
  }
}

/**
 * Central auth portal origin for login redirects.
 * Prefer the domain family of the current page (smart-buildings vs smarterbuildings) so a
 * cached Docker build cannot send local iot.smart-buildings.ca users to cloud smarterbuildings.
 * REACT_APP_CENTRAL_AUTH_URL still wins when set at build time for special overrides.
 */
function getCentralAuthOrigin() {
  if (typeof window !== "undefined") {
    const h = window.location.hostname.toLowerCase();
    const protocol = window.location.protocol || "https:";
    if (h.includes("smart-buildings.ca")) {
      return `${protocol}//home.smart-buildings.ca`.replace(/\/$/, "");
    }
    if (h.includes("smarterbuildings.ca")) {
      return `${protocol}//home.smarterbuildings.ca`.replace(/\/$/, "");
    }
    if (h === "localhost" || h === "127.0.0.1") {
      const local =
        (typeof process !== "undefined" && process.env?.REACT_APP_CENTRAL_AUTH_URL) ||
        "http://localhost:4000";
      return String(local).replace(/\/$/, "");
    }
  }
  const fromEnv =
    (typeof process !== "undefined" && process.env?.REACT_APP_CENTRAL_AUTH_URL) ||
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_CENTRAL_AUTH_URL);
  if (fromEnv) return String(fromEnv).replace(/\/$/, "");
  return "https://home.smarterbuildings.ca";
}

function centralAuthHostname() {
  try {
    return new URL(getCentralAuthOrigin()).hostname;
  } catch {
    if (typeof window !== "undefined" && window.location.hostname.includes("smart-buildings.ca")) {
      return "home.smart-buildings.ca";
    }
    return "home.smarterbuildings.ca";
  }
}

function currentAppReturnPath() {
  if (typeof window === "undefined") return "/";
  const url = new URL(window.location.href);
  // Never carry auth debugging flags back through central auth.
  url.searchParams.delete("debug");
  url.searchParams.delete("logout");
  url.searchParams.delete("signedOut");
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Same redirect target as automatic login redirect; preserves return to this app (path + query). */
function buildCentralAuthUrl() {
  if (typeof window === "undefined") {
    const base = getCentralAuthOrigin();
    return `${base}/`;
  }
  const returnPath = currentAppReturnPath();
  const returnUrl = encodeURIComponent(window.location.origin + returnPath);
  const base = getCentralAuthOrigin();
  return `${base}?returnUrl=${returnUrl}`;
}

/**
 * Central auth portal only — no `returnUrl`.
 * Use for the top-bar “Portal” link: if we included returnUrl, many deployments immediately
 * redirect logged-in users back to IoT, which feels like a broken / refreshing link.
 */
function buildCentralAuthPortalUrl() {
  const base = getCentralAuthOrigin();
  return `${base}/`;
}

/** Drop stale JWT/user so central auth can issue a fresh session; then open login. */
function clearStoredSessionAndOpenCentralAuth() {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("authDebug");
    sessionStorage.removeItem("authRedirectAttempt");
    sessionStorage.removeItem("authRedirectCount");
    sessionStorage.removeItem("iotAuthPauseRedirect");
  } catch {
    /* ignore */
  }
  try {
    logUiEvent("Auth", "Opening Central Auth (cleared saved IoT session first)");
  } catch {
    /* ignore */
  }
  window.location.assign(buildCentralAuthUrl());
}

/** Same as initializeAuth’s debug check — must stay in sync. */
function isAuthDebugMode() {
  if (typeof window === "undefined") return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("debug") === "true") return true;
    if (localStorage.getItem("authDebug") === "true") return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * When the session is invalid, send users through Central Auth (same path as “no token”).
 * Avoids trapping people on a modal if clicks are blocked by stacking/context issues.
 */
function replaceWithCentralAuthLogin() {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    sessionStorage.removeItem("authRedirectAttempt");
    sessionStorage.removeItem("authRedirectCount");
  } catch {
    /* ignore */
  }
  try {
    logUiEvent("Auth", "Session invalid — redirecting to Central Auth");
  } catch {
    /* ignore */
  }
  window.location.replace(buildCentralAuthUrl());
}

export default function App() {
  // Get token from URL query parameter (from central auth redirect)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Initialize authentication from URL token or existing session
  useEffect(() => {
    /** When true, we navigate away to Central Auth — do not set loading false (avoids flashing the wrong screen). */
    let pendingCentralAuthRedirect = false;

    /**
     * @param {{ clearSessionRedirectMarkers?: boolean }} [opts]
     * When false, keeps authRedirectCount so we can detect loops if Central Auth returns without a token.
     * When true (default), clears markers — use after a rejected/invalid stored token.
     */
    const redirectToCentralAuthForLogin = (opts = {}) => {
      const clearSessionRedirectMarkers = opts.clearSessionRedirectMarkers !== false;
      pendingCentralAuthRedirect = true;
      try {
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        if (clearSessionRedirectMarkers) {
          sessionStorage.removeItem("authRedirectAttempt");
          sessionStorage.removeItem("authRedirectCount");
        }
        sessionStorage.removeItem("iotAuthPauseRedirect");
      } catch {
        /* ignore */
      }
      try {
        logUiEvent("Auth", "Redirecting to Central Auth (login required)");
      } catch {
        /* ignore */
      }
      window.location.replace(buildCentralAuthUrl());
    };

    const initializeAuth = async () => {
      if (typeof window === "undefined") return;

      const getAuthMeUrl = async () => {
        const { API_BASE } = await import("./services/api");
        let authUrl = `${API_BASE}/api/auth/me`;
        if (authUrl.includes("/control")) authUrl = authUrl.replace("/control", "/central");
        return authUrl;
      };

      const fetchCurrentUser = async (authToken = null) => {
        const authUrl = await getAuthMeUrl();
        const headers = {
          "Content-Type": "application/json",
        };
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }
        console.log("[Auth] Fetching user info from:", authUrl);
        console.log(
          "[Auth] Session source:",
          authToken ? `local token (${authToken.substring(0, 20)}...)` : "shared auth cookie"
        );
        const response = await fetch(authUrl, {
          method: "GET",
          headers,
          credentials: "include",
        });
        return response;
      };

      // Check for debug mode - prevents redirects and shows errors
      const urlParams = new URLSearchParams(window.location.search);
      // Central auth or ops can redirect here with ?logout=1 to clear a stale IoT token
      if (urlParams.get('logout') === '1' || urlParams.get('signedOut') === '1') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      const pauseRedirectMode = sessionStorage.getItem("iotAuthPauseRedirect") === "true";
      const debugMode =
        urlParams.get('debug') === 'true' ||
        localStorage.getItem('authDebug') === 'true';
      if (debugMode) {
        console.log(
          pauseRedirectMode
            ? "[Auth] REDIRECT PAUSE MODE ENABLED - Auto-redirects disabled after loop detection"
            : "[Auth] DEBUG MODE ENABLED - Redirects disabled"
        );
      }

      // Prevent redirect loops - if we're already on the central auth *origin*, don't redirect.
      // Compare full origin (scheme+host+port), not hostname alone: localhost:8123 (this app)
      // and localhost:4000 (central auth) share a hostname and must not be treated as the same page.
      let onCentralAuthOrigin = false;
      try {
        onCentralAuthOrigin = window.location.origin === new URL(getCentralAuthOrigin()).origin;
      } catch {
        onCentralAuthOrigin = window.location.hostname === centralAuthHostname();
      }
      if (onCentralAuthOrigin) {
        console.log("[Auth] Already on central auth page, not redirecting");
        setIsLoadingAuth(false);
        setIsAuthenticated(false);
        return;
      }

      // Check for token in URL query parameter (from central auth redirect)
      // Central auth might use different parameter names: token, access_token, jwt, etc.
      // Also check cookies (some auth systems use cookies)
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
      }, {});

      // OAuth-style implicit flow sometimes puts tokens in the hash (not sent to server)
      let tokenFromHash = null;
      try {
        const rawHash = window.location.hash?.replace(/^#/, '') || '';
        if (rawHash && rawHash.includes('=')) {
          const hp = new URLSearchParams(rawHash);
          tokenFromHash =
            hp.get('access_token') || hp.get('token') || hp.get('id_token') || null;
        }
      } catch {
        tokenFromHash = null;
      }
      
      const tokenFromUrl = urlParams.get("token") || 
                          urlParams.get("access_token") || 
                          urlParams.get("jwt") ||
                          urlParams.get("authToken") ||
                          urlParams.get("t") || // Sometimes shortened
                          tokenFromHash ||
                          cookies.token || // Check cookies
                          cookies.authToken ||
                          cookies.jwt;

      console.log("[Auth] Checking for token:", {
        hasTokenInUrl: !!tokenFromUrl && (urlParams.get("token") || urlParams.get("access_token") || urlParams.get("jwt") || urlParams.get("authToken") || urlParams.get("t")),
        hasTokenInCookie: !!(cookies.token || cookies.authToken || cookies.jwt),
        tokenLength: tokenFromUrl?.length,
        urlParams: Object.fromEntries(urlParams.entries()),
        cookieKeys: Object.keys(cookies)
      });

      if (tokenFromUrl) {
        console.log("[Auth] Token found in URL, storing...");
        // Store token from URL
        localStorage.setItem("authToken", tokenFromUrl);
        // Remove token from URL (query + hash) for security
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }

      // Get token from localStorage
      const token = localStorage.getItem("authToken");
      console.log("[Auth] Token from localStorage:", token ? `Present (${token.length} chars)` : "Not found");
      
      if (!token) {
        console.log("[Auth] No local token found; trying shared auth cookie bootstrap");
        try {
          let cookieBootstrapResponse = await fetchCurrentUser(null);
          if (cookieBootstrapResponse.status === 503) {
            console.warn("[Auth] Cookie bootstrap got 503, retrying once...");
            await new Promise((resolve) => setTimeout(resolve, 1200));
            cookieBootstrapResponse = await fetchCurrentUser(null);
          }
          // Refresh-token rotation can race across parallel requests:
          // one request refreshes cookies while another still sees stale auth_token.
          // Give it one short retry window before treating this as a hard auth failure.
          if (cookieBootstrapResponse.status === 401) {
            console.warn("[Auth] Cookie bootstrap got 401, retrying once after short delay...");
            await new Promise((resolve) => setTimeout(resolve, 900));
            cookieBootstrapResponse = await fetchCurrentUser(null);
          }
          console.log("[Auth] Cookie bootstrap response status:", cookieBootstrapResponse.status);
          if (cookieBootstrapResponse.ok) {
            const data = await cookieBootstrapResponse.json();
            if (data.success && data.user) {
              setCurrentUser(data.user);
              setIsAuthenticated(true);
              localStorage.setItem("user", JSON.stringify(data.user));
              sessionStorage.removeItem("authRedirectAttempt");
              sessionStorage.removeItem("authRedirectCount");
              sessionStorage.removeItem("iotAuthPauseRedirect");
              setIsLoadingAuth(false);
              console.log("[Auth] Cookie bootstrap successful");
              return;
            }
          } else if (cookieBootstrapResponse.status === 503) {
            const errorText = await cookieBootstrapResponse.text().catch(() => "No error details");
            console.error("[Auth] Cookie bootstrap unavailable (503):", errorText);
            try {
              sessionStorage.removeItem("authRedirectAttempt");
              sessionStorage.removeItem("authRedirectCount");
              sessionStorage.removeItem("iotAuthPauseRedirect");
            } catch {
              /* ignore */
            }
            setIsLoadingAuth(false);
            setIsAuthenticated(false);
            setAuthError({
              message: "Session check temporarily unavailable",
              details:
                "The dashboard could not verify your session with the authentication service. This is usually temporary. Try again in a few seconds. Your sign-in was not cleared.",
              canRetry: true,
            });
            return;
          }
        } catch (cookieBootstrapError) {
          console.error("[Auth] Cookie bootstrap failed:", cookieBootstrapError);
          if (cookieBootstrapError.message === "AUTH_VERIFY_UNAVAILABLE_503") {
            throw cookieBootstrapError;
          }
        }

        console.log("[Auth] No token found");
        
        // Check redirect attempt count (prevent infinite loop)
        const lastRedirectTime = parseInt(sessionStorage.getItem("authRedirectAttempt") || "0", 10);
        const timeSinceAttempt = Date.now() - lastRedirectTime;
        let redirectCount = parseInt(sessionStorage.getItem("authRedirectCount") || "0", 10);
        // Stale window: user came back minutes later — don't treat as a loop
        if (timeSinceAttempt > 60000) {
          redirectCount = 0;
          sessionStorage.removeItem("authRedirectCount");
        }
        
        // If we've redirected several times in a short window, stop (avoid false positives on slow networks)
        if (redirectCount >= 4 && timeSinceAttempt < 60000) {
          console.error("[Auth] Redirect loop detected! Stopping automatic redirect after", redirectCount, "attempts");
          sessionStorage.setItem("iotAuthPauseRedirect", "true");
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
          setAuthError({
            message: "Could not complete sign-in",
            details:
              "The app tried to send you to Central Authentication several times but no valid session was returned to the IoT dashboard. Use Go to Central Auth below, sign in there, and you should return with access. If this keeps happening, check that Central Auth is configured to pass a token in the redirect URL for this app.",
            canRetry: true,
          });
          return;
        }

        // Don't redirect in debug mode
        if (debugMode) {
          console.error("[Auth] DEBUG MODE: No token found, but not redirecting");
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
          setAuthError({
            message: pauseRedirectMode ? "Automatic redirect paused" : "No Authentication Token Found",
            details:
              pauseRedirectMode
                ? "The dashboard detected an authentication redirect loop and paused automatic redirects so you do not get bounced back and forth again.\n\nTo continue: use Go to Central Auth below for a deliberate sign-in attempt, or enable debug mode if you want to inspect the current auth state without any redirects."
                : "Auth debug mode is ON (you have ?debug=true in the URL and/or localStorage authDebug=true). In that mode the app does not redirect you to central login automatically, so you can see this screen with an empty session.\n\nTo sign in from here: use Go to Central Auth below, complete login, and you should return with a token.\n\nTo use normal behavior instead: turn off debug (remove ?debug=true and clear authDebug), then reload — the app will redirect to central auth by itself.",
            canRetry: true,
            debugStuckNoToken: true,
          });
          return;
        }
        
        // No local token — require Central Auth (same as other apps: direct URL access is denied until signed in)
        console.log("[Auth] No token found, redirecting to central auth");
        sessionStorage.setItem("authRedirectAttempt", String(Date.now()));
        sessionStorage.setItem("authRedirectCount", String(redirectCount + 1));
        redirectToCentralAuthForLogin({ clearSessionRedirectMarkers: false });
        return;
      }
      
      // Clear redirect attempt markers if we have a token
      sessionStorage.removeItem("authRedirectAttempt");
      sessionStorage.removeItem("authRedirectCount");
      sessionStorage.removeItem("iotAuthPauseRedirect");
      
      // Clear redirect attempt marker if we have a token
      sessionStorage.removeItem("authRedirectAttempt");

      // Fetch user info from central server
      try {
        const response = await fetchCurrentUser(token);

        console.log("[Auth] Response status:", response.status);
        console.log("[Auth] Response headers:", Object.fromEntries(response.headers.entries()));

        if (response.ok) {
          const data = await response.json();
          console.log("[Auth] User data received:", data);
          if (data.success && data.user) {
            if (data.access_token) {
              localStorage.setItem("authToken", data.access_token);
            }
            setCurrentUser(data.user);
            setIsAuthenticated(true);
            localStorage.setItem("user", JSON.stringify(data.user));
            console.log("[Auth] Authentication successful");
          } else {
            throw new Error("Invalid user data: " + JSON.stringify(data));
          }
        } else if (response.status === 401) {
          const errorText = await response.text().catch(() => "No error details");
          console.error("[Auth] Token expired or invalid (401):", errorText);
          
          // Don't redirect in debug mode
          if (debugMode) {
            setIsLoadingAuth(false);
            setIsAuthenticated(false);
            setAuthError({
              message: "Authentication Failed (401 Unauthorized)",
              details: `The API rejected your saved token (session invalid or revoked).\n\n${errorText}\n\nWith debug mode on, the app keeps that token in the browser, so Try Again just fails the same way. Use Sign in again or Go to Central Auth below — both clear the token first.`,
              canRetry: true,
              invalidSession401: true,
              clearedRejectedToken: false,
            });
            return;
          }
          
          // Normal UX: invalid/revoked token — same as “no token” (returnUrl preserves this app).
          setIsAuthenticated(false);
          setCurrentUser(null);
          redirectToCentralAuthForLogin();
          return;
        } else if (response.status === 503) {
          const errorText = await response.text().catch(() => "No error details");
          console.error("[Auth] Session verification unavailable (503):", errorText);
          throw new Error("AUTH_VERIFY_UNAVAILABLE_503");
        } else {
          const errorText = await response.text().catch(() => "No error details");
          console.error(`[Auth] Failed to fetch user: ${response.status}`, errorText);
          throw new Error(`Failed to fetch user: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        // Log full error details
        console.error("[Auth] Auth initialization error:", error);
        console.error("[Auth] Error name:", error.name);
        console.error("[Auth] Error message:", error.message);
        console.error("[Auth] Error stack:", error.stack);
        
        // Store error details for display
        const errorDetails = {
          name: error.name,
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        };
        sessionStorage.setItem("lastAuthError", JSON.stringify(errorDetails));

        // Auth reachable but could not verify session (e.g. iot-central-server ↔ auth blip) — keep token, allow retry
        if (error.message === "AUTH_VERIFY_UNAVAILABLE_503") {
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
          setAuthError({
            message: "Session check temporarily unavailable",
            details:
              "The dashboard could not verify your session with the authentication service. This is usually temporary. Try again in a few seconds. Your sign-in was not cleared.",
            canRetry: true,
          });
          return;
        }
        
        // Don't redirect on network errors - might be a temporary issue or wrong API URL
        if (
          error.message &&
          (error.message.includes("Failed to fetch") ||
            error.message.includes("ERR_SSL") ||
            error.message.includes("CORS") ||
            error.message.includes("SSL") ||
            error.name === "TypeError")
        ) {
          console.error("[Auth] Network/SSL/CORS error - not redirecting, showing error");
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
          setAuthError({
            message: "Connection Error",
            details: `Cannot connect to API server. Error: ${error.message}. This might be due to: 1) Wrong API URL, 2) SSL certificate issues, 3) CORS configuration. Check console (F12) for details.`,
            canRetry: true,
            error: errorDetails
          });
          return;
        }
        
        // Don't redirect in debug mode
        if (debugMode) {
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
          setAuthError({
            message: "Authentication Error",
            details: `${error.name}: ${error.message}. Check console (F12) for full details.`,
            canRetry: true,
            error: errorDetails
          });
          return;
        }
        
        // Session could not be verified — send user to Central Auth (not logged in for this app)
        redirectToCentralAuthForLogin();
        return;
      } finally {
        if (!pendingCentralAuthRedirect) {
          setIsLoadingAuth(false);
        }
      }
    };

    initializeAuth();
  }, []);

  // API layer (jsonFetch) signals expired session — redirect to Central Auth unless debugging
  useEffect(() => {
    const onExpired = () => {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      setCurrentUser(null);
      if (!isAuthDebugMode()) {
        replaceWithCentralAuthLogin();
        return;
      }
      setAuthError({
        message: "Session Expired",
        details:
          "Your session ended while you were away, or the server rejected your token. Use “Go to Central Auth” below to sign in again.",
        canRetry: true,
      });
    };
    window.addEventListener("iot-auth-expired", onExpired);
    return () => window.removeEventListener("iot-auth-expired", onExpired);
  }, []);

  // Revalidate session when returning to this tab (logout elsewhere revokes access token on server)
  useEffect(() => {
    if (!isAuthenticated || isLoadingAuth) return;

    let revalidateInFlight = false;
    let lastRevalidateAt = 0;
    let lastHiddenAt = Date.now();

    const MIN_REVALIDATE_GAP_MS = 5000;
    const HIDDEN_REVALIDATE_THRESHOLD_MS = 15000;

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const revalidate = async () => {
      if (document.visibilityState !== 'visible' || revalidateInFlight) return;
      const now = Date.now();
      if (now - lastRevalidateAt < MIN_REVALIDATE_GAP_MS) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;
      revalidateInFlight = true;
      lastRevalidateAt = now;
      try {
        const { API_BASE } = await import('./services/api');
        let authMeUrl = `${API_BASE}/api/auth/me`;
        if (authMeUrl.includes('/control')) authMeUrl = authMeUrl.replace('/control', '/central');
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        const fetchAuthMe = () => fetch(authMeUrl, { headers });
        let response = await fetchAuthMe();
        if (response.status === 401) {
          // A single focus-time 401 can be a transient race while auth services recover.
          // Retry once briefly before clearing the session and bouncing to Central Auth.
          await sleep(800);
          response = await fetchAuthMe();
        }
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setCurrentUser(null);
          if (!isAuthDebugMode()) {
            replaceWithCentralAuthLogin();
            return;
          }
          setAuthError({
            message: 'Session ended',
            details:
              'You were signed out or your session was revoked. Please sign in again from central authentication.',
            canRetry: true,
          });
          return;
        }
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setCurrentUser(data.user);
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        }
      } catch (e) {
        console.warn('[Auth] Tab revalidation failed:', e);
      } finally {
        revalidateInFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt = Date.now();
        return;
      }
      if (Date.now() - lastHiddenAt < HIDDEN_REVALIDATE_THRESHOLD_MS) {
        return;
      }
      void revalidate();
    };

    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      void revalidate();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated, isLoadingAuth]);

  const [tab, setTab] = useState("dashboard");
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState(
    localStorage.getItem("selectedBuildingId") || "all"
  );
  const { toasts, showToast, removeToast } = useToast();
  const [, setIsFullscreenOpen] = useState(false);
  const [selectedDevEui, setSelectedDevEui] = useState(
    localStorage.getItem("selectedDevEui") || ""
  );
  const [selectedDraginoCardRole, setSelectedDraginoCardRole] = useState(() => readStoredDraginoCardRole());
  const [shellDarkMode, setShellDarkMode] = useState(readStoredShellDarkMode);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (isAuthenticated && !isLoadingAuth) {
      document.body.classList.add("iot-dashboard-shell-active");
    } else {
      document.body.classList.remove("iot-dashboard-shell-active");
    }
    return () => document.body.classList.remove("iot-dashboard-shell-active");
  }, [isAuthenticated, isLoadingAuth]);

  // Devices that are hidden from the main dashboard & top select
  const [hiddenDevs, setHiddenDevs] = useState(() => {
    try {
      const raw = localStorage.getItem("hiddenDevices");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Phantom EUI returned by backend but not in ChirpStack - exclude from UI
  const PHANTOM_EUI = "24e124715c427915";

  // Load devices only after auth succeeds — avoids racing /api/devices 401 with initializeAuth
  // and prevents jsonFetch 401 + reload from fighting the login redirect flow (AFK redirect loops).
  useEffect(() => {
    if (!isAuthenticated || isLoadingAuth) return;
    let cancelled = false;
    (async () => {
      setDevicesLoading(true);
      try {
        const [list, buildingList] = await Promise.all([fetchDevices(), fetchBuildings().catch(() => [])]);
        if (cancelled) return;

        const rawDevices = Array.isArray(list) ? list : [];
        const filtered = rawDevices.filter((d) => d?.devEui !== PHANTOM_EUI);
        const withTypes = filtered.map((d) => ({
          ...d,
          buildingId: getDeviceBuildingId(d),
          type: normalizeType(d.sensorType),
        }));
        setDevices(withTypes);
        const normalizedBuildings = Array.isArray(buildingList)
          ? buildingList.map(normalizeBuildingRecord)
          : [];
        setBuildings(normalizedBuildings);
        const saved = localStorage.getItem("selectedBuildingId");
        if (
          saved &&
          saved !== "all" &&
          !withTypes.some((device) => buildingIdsMatch(getDeviceBuildingId(device), saved)) &&
          !normalizedBuildings.some((building) => buildingIdsMatch(getBuildingRecordId(building), saved))
        ) {
          setSelectedBuildingId("all");
          localStorage.removeItem("selectedBuildingId");
        } else if (
          saved === "all" &&
          normalizedBuildings.length === 1 &&
          Number(currentUser?.dashboardRoleLevel ?? 1) < 5
        ) {
          const onlyBuildingId = getBuildingRecordId(normalizedBuildings[0]);
          if (onlyBuildingId) {
            setSelectedBuildingId(onlyBuildingId);
          }
        }

        setSelectedDevEui((prev) => {
          if (prev === PHANTOM_EUI) {
            localStorage.removeItem("selectedDevEui");
            return "";
          }
          return prev;
        });
        // Don't auto-select first device - show summary home by default
      } catch (e) {
        console.error("Failed to load devices", e);
      } finally {
        if (!cancelled) setDevicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoadingAuth, currentUser?.dashboardRoleLevel]);

  // Persist hidden devices so the dashboard remembers user choices
  useEffect(() => {
    try {
      localStorage.setItem("hiddenDevices", JSON.stringify(hiddenDevs));
    } catch {
      // ignore quota errors—hiding list is small
    }
  }, [hiddenDevs]);

  // Persist selected building
  useEffect(() => {
    localStorage.setItem("selectedBuildingId", selectedBuildingId);
  }, [selectedBuildingId]);

  // Redirect if user lacks access to Admin (Level 5 only) or Users (Level 4+). Alarms: all levels.
  useEffect(() => {
    const level = currentUser?.dashboardRoleLevel ?? 1;
    if (level < 5 && tab === "logs") {
      setTab("dashboard");
    }
    if (level < 4 && tab === "users") {
      setTab("dashboard");
    }
    if (level < 5 && tab === "sensor-categories") {
      setTab("dashboard");
    }
    if (level < 5 && (tab === "controller-io" || tab === "automations")) {
      setTab("dashboard");
    }
  }, [currentUser, tab]);

  // Devices that are visible in the dashboard & dropdown (filtered by building + hidden list).
  // Sorted by natural alphanumeric name so lists stay consistent (M-2 before M-10, groups together).
  const visibleDevices = useMemo(() => {
    const filtered = devices.filter((d) => {
      if (hiddenDevs.includes(d.devEui)) return false;
      if (
        selectedBuildingId !== "all" &&
        !buildingIdsMatch(getDeviceBuildingId(d), selectedBuildingId)
      ) {
        return false;
      }
      return true;
    });
    return sortDevicesByNaturalName(filtered);
  }, [devices, hiddenDevs, selectedBuildingId]);

  const selected = useMemo(
    () => visibleDevices.find((d) => d.devEui === selectedDevEui) || null,
    [visibleDevices, selectedDevEui]
  );

  const handleDeleteDevice = async (devEui) => {
    const result = await deleteDevice(devEui);
    setDevices((prev) => prev.filter((d) => d.devEui !== devEui));
    setHiddenDevs((prev) => prev.filter((hiddenDevEui) => hiddenDevEui !== devEui));
    setSelectedDevEui((prev) => (prev === devEui ? "" : prev));
    setSelectedDraginoCardRole(null);
    writeStoredDraginoCardRole(null);
    if (localStorage.getItem("selectedDevEui") === devEui) {
      localStorage.removeItem("selectedDevEui");
    }
    showToast(result?.message || "Device deleted successfully", "success");
    return result;
  };

  const handleDeviceLocationSaved = (devEui, location) => {
    setDevices((prev) =>
      prev.map((d) => (d.devEui === devEui ? { ...d, location: location || null } : d))
    );
  };

  // Breadcrumb items based on current state
  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        label: "Home",
        onClick: () => {
          logUiEvent("Navigation", "Breadcrumb: Home", "cleared selected device");
          setTab("dashboard");
          setSelectedDevEui("");
          setSelectedDraginoCardRole(null);
          writeStoredDraginoCardRole(null);
          localStorage.removeItem("selectedDevEui");
        },
      },
    ];

    if (tab === "dashboard") {
      if (selected) {
        items.push({
          label: "Devices",
          onClick: () => {
            logUiEvent("Navigation", "Breadcrumb: Devices", "cleared selected device");
            setTab("dashboard");
            setSelectedDevEui("");
            setSelectedDraginoCardRole(null);
            writeStoredDraginoCardRole(null);
            localStorage.removeItem("selectedDevEui");
          },
        });
        items.push({
          label: selected.name || selected.devEui,
          onClick: null, // Current page, not clickable
        });
      } else {
        items.push({
          label: "Devices",
          onClick: null,
        });
      }
    } else if (tab === "alarms") {
      items.push({
        label: "Alarms",
        onClick: null, // Current page, not clickable
      });
    } else if (tab === "logs") {
      items.push({
        label: "Admin",
        onClick: null,
      });
    } else if (tab === "sitings") {
      items.push({
        label: "Sites",
        onClick: null,
      });
    } else if (tab === "users") {
      items.push({
        label: "Users",
        onClick: null,
      });
    } else if (tab === "sensor-categories") {
      items.push({
        label: "Sensor categories",
        onClick: null,
      });
    } else if (tab === "thermostat-groups") {
      items.push({
        label: "Thermostat groups",
        onClick: null,
      });
    } else if (tab === "controller-io") {
      items.push({
        label: "Controller I/O",
        onClick: null,
      });
    } else if (tab === "automations") {
      items.push({
        label: "Automations",
        onClick: null,
      });
    }

    return items;
  }, [tab, selected]);

  // If the currently selected device gets hidden, fall back to first visible (only when we had one selected)
  useEffect(() => {
    if (selectedDevEui && !selected && visibleDevices.length > 0) {
      const next = visibleDevices[0].devEui;
      setSelectedDevEui(next);
      setSelectedDraginoCardRole(null);
      writeStoredDraginoCardRole(null);
      localStorage.setItem("selectedDevEui", next);
    }
  }, [selectedDevEui, selected, visibleDevices]);

  const goHome = () => {
    logUiEvent("Navigation", "Shell: Home");
    setTab("dashboard");
    setSelectedDevEui("");
    setSelectedDraginoCardRole(null);
    writeStoredDraginoCardRole(null);
    try { localStorage.removeItem("selectedDevEui"); } catch {}
  };

  const goLogs = () => {
    logUiEvent("Navigation", "Shell: Admin");
    setTab("logs");
  };

  const goSites = () => {
    logUiEvent("Navigation", "Shell: Sites");
    setTab("sitings");
    setSelectedDevEui("");
    setSelectedDraginoCardRole(null);
    writeStoredDraginoCardRole(null);
    try { localStorage.removeItem("selectedDevEui"); } catch {}
  };

  const goAlarms = () => {
    logUiEvent("Navigation", "Shell: Alarms");
    setTab("alarms");
  };

  const goUsers = () => {
    logUiEvent("Navigation", "Shell: Administrator settings");
    setTab("users");
  };

  const goSensorCategories = () => {
    logUiEvent("Navigation", "Shell: Sensor categories");
    setTab("sensor-categories");
  };

  const goThermostatGroups = () => {
    logUiEvent("Navigation", "Shell: Thermostat groups");
    setTab("thermostat-groups");
  };

  const goControllerIo = () => {
    logUiEvent("Navigation", "Shell: Controller I/O");
    setTab("controller-io");
  };

  const goAutomations = () => {
    logUiEvent("Navigation", "Shell: Automations");
    setTab("automations");
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("loginTime");
      sessionStorage.removeItem("authRedirectAttempt");
      sessionStorage.removeItem("authRedirectCount");
      sessionStorage.removeItem("iotAuthPauseRedirect");
      logUiEvent("Auth", "Logging out from IoT dashboard");
    } catch {
      /* ignore */
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    window.location.assign(buildCentralAuthPortalUrl());
  };

  const handleShellDarkModeChange = (enabled) => {
    setShellDarkMode(enabled);
    try {
      localStorage.setItem("inAppDashboardDarkMode", String(enabled));
      localStorage.setItem("fm_theme", enabled ? "dark" : "light");
    } catch {
      /* ignore */
    }
  };

  const handleShellNavigate = (route) => {
    if (!route) return;
    if (route === "home" || route === "sensors") {
      goHome();
      return;
    }
    if (route === "logs") {
      goLogs();
      return;
    }
    if (route === "sites") {
      goSites();
      return;
    }
    if (route === "alarms") {
      goAlarms();
      return;
    }
    if (route === "users") {
      goUsers();
      return;
    }
    if (route === "sensor-categories") {
      goSensorCategories();
      return;
    }
    if (route === "thermostat-groups") {
      goThermostatGroups();
      return;
    }
    if (route === "controller-io") {
      goControllerIo();
      return;
    }
    if (route === "automations") {
      goAutomations();
      return;
    }
    window.location.assign(route);
  };

  const currentRoleLevel = currentUser?.dashboardRoleLevel ?? 1;
  const roleLabel = formatIotDashboardRoleLabel(currentRoleLevel);

  const buildingSelector = buildings.length > 0 ? (
    <SummaryFilterSelect
      id="buildingSel"
      label="Building"
      icon="building"
      value={selectedBuildingId}
      options={[
        ...(buildings.length > 1 || currentRoleLevel >= 5 ? [{ value: "all", label: "All Buildings" }] : []),
        ...buildings.map((b) => ({
          value: getBuildingRecordId(b),
          label: b.name,
        })),
      ]}
      onChange={(newBuildingId) => {
        const buildingLabel =
          newBuildingId === "all"
            ? "All buildings"
            : buildings.find((b) => getBuildingRecordId(b) === newBuildingId)?.name || newBuildingId;
        logUiEvent("Filter", `Building: ${buildingLabel}`, `buildingId=${newBuildingId}`);
        setSelectedBuildingId(newBuildingId);
        const currentDevice = devices.find((d) => d.devEui === selectedDevEui);
        if (newBuildingId !== "all" && currentDevice && !buildingIdsMatch(getDeviceBuildingId(currentDevice), newBuildingId)) {
          const firstInBuilding = devices.find((d) => buildingIdsMatch(getDeviceBuildingId(d), newBuildingId));
          if (firstInBuilding) {
            setSelectedDevEui(firstInBuilding.devEui);
            localStorage.setItem("selectedDevEui", firstInBuilding.devEui);
          }
        }
      }}
    />
  ) : null;

  const shellConfig = {
    appName: "Building Automation & IoT",
    logo: { src: "/assets/logos/Smarterbuildings-IOT.png", alt: "Building Automation & IoT" },
    homeRoute: "home",
    mainDashboardRoute: buildCentralAuthPortalUrl(),
    theme: {
      topNavBackground: "#ffffff",
      topNavText: "#0B3C60",
      sideNavBackground: "#301D5C",
      sideNavActiveBackground: "rgba(140, 198, 63, 0.24)",
      sideNavActiveText: "#ffffff",
      sideNavHoverBackground: "rgba(255, 255, 255, 0.14)",
      accent: "#301D5C",
      border: "#d7dde7",
      logoAreaBackground: "#ffffff",
      appSwitcherBackground: "#ffffff",
      appSwitcherHoverBackground: "#f3f8f5",
      appBackground: "#f8fafc",
      surface: "#ffffff",
      surfaceMuted: "#f3f4f6",
      text: "#111827",
      textMuted: "#64748b",
      cardBackground: "#ffffff",
      cardText: "#111827",
      darkBackground: "#1f1f24",
      darkSurface: "#2a2a32",
      darkSurfaceMuted: "#33333d",
      darkText: "#f8fafc",
      darkTextMuted: "#cbd5e1",
      darkBorder: "rgba(255, 255, 255, 0.14)",
      darkCardBackground: "#2a2a32",
      darkCardText: "#f8fafc",
    },
    switcherItems: [
      {
        id: "iot",
        label: "Building Automation & IoT",
        logo: { src: "/assets/logos/Smarterbuildings-IOT.png", alt: "" },
        route: "home",
      },
    ],
  };
  const shellNavItems = [
    { id: "home", label: "Home", icon: "home", active: tab === "dashboard" && !selected, route: "home" },
    { id: "logs", label: "Admin", icon: "admin_panel_settings", active: tab === "logs", visible: currentRoleLevel >= 5, route: "logs" },
    { id: "sites", label: "Sites", icon: "domain", active: tab === "sitings", route: "sites" },
    { id: "alarms", label: "Alarms", icon: "notifications_active", active: tab === "alarms", route: "alarms" },
    {
      id: "thermostat-groups",
      label: "Thermo groups",
      icon: "group_work",
      active: tab === "thermostat-groups",
      route: "thermostat-groups",
    },
    {
      id: "controller-io",
      label: "Controllers",
      icon: "settings_input_component",
      active: tab === "controller-io",
      visible: currentRoleLevel >= 5,
      route: "controller-io",
    },
    {
      id: "automations",
      label: "Automations",
      icon: "bolt",
      active: tab === "automations",
      visible: currentRoleLevel >= 5,
      route: "automations",
    },
  ];
  const shellAccount = {
    displayName: currentUser?.name || currentUser?.username || currentUser?.email,
    email: currentUser?.email || currentUser?.username,
    menuItems: [
      {
        id: "role",
        label: roleLabel || `Level ${currentRoleLevel}`,
        icon: "admin_panel_settings",
        disabled: true,
        visible: currentUser != null,
      },
      {
        id: "admin-settings",
        label: "Administrator settings",
        icon: "manage_accounts",
        route: "users",
        visible: currentRoleLevel >= 4,
      },
      {
        id: "sensor-categories",
        label: "Sensor categories",
        icon: "category",
        route: "sensor-categories",
        visible: currentRoleLevel >= 5,
      },
      {
        id: "sign-out",
        label: "Log out",
        icon: "logout",
        danger: true,
        onSelect: handleLogout,
      },
    ],
  };

  return (
    <ThemeProvider theme={shellDarkMode ? "dark" : "light"}>
      {/* useToast was already wired throughout the app but its container was never
          rendered, so every showToast call was silently discarded. */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {isLoadingAuth ? (
        <div className="app" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
          <LoadingSpinner message="Authenticating..." fullScreen />
        </div>
      ) : isAuthenticated ? (
        <DashboardShell
          config={shellConfig}
          navItems={shellNavItems}
          account={shellAccount}
          darkMode={shellDarkMode}
          defaultDarkMode={false}
          darkModeStorageKey="inAppDashboardDarkMode"
          onDarkModeChange={handleShellDarkModeChange}
          onNavigate={handleShellNavigate}
          onAlertsClick={goAlarms}
          bodyDarkModeClassName="iot-dashboard-dark"
          className="iot-dashboard-shell"
        >
          {tab === "dashboard" && (
            <>
              <Breadcrumb items={breadcrumbItems} />
              {!selected && (
                <Suspense fallback={<LoadingSpinner message="Loading sensors..." />}>
                  <SensorSummaryHome
                    devices={visibleDevices}
                    currentUser={currentUser}
                    onDeleteDevice={handleDeleteDevice}
                    buildings={buildings}
                    isLoading={devicesLoading}
                    buildingSelector={buildingSelector}
                    onSelectDevice={(d, opts) => {
                      const intent = opts?.intent === "quick" ? "quick" : "details";
                      const cardRole = d.cardRole || null;
                      logUiEvent(
                        "Navigation",
                        intent === "quick" ? "Quick control from home grid" : "Opened device from home grid",
                        `${d.name || d.devEui} (${d.devEui}) type=${normalizeType(d.sensorType)} cardRole=${cardRole || "default"} intent=${intent}`
                      );
                      setSelectedDevEui(d.devEui);
                      setSelectedDraginoCardRole(cardRole);
                      writeStoredDraginoCardRole(cardRole);
                      localStorage.setItem("selectedDevEui", d.devEui);
                    }}
                  />
                </Suspense>
              )}

              {selected?.type === "water" && (
                <Suspense fallback={<LoadingSpinner message="Loading water dashboard..." />}>
                  <WaterDashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "thermostat" && (
                <Suspense fallback={<LoadingSpinner message="Loading thermostat dashboard..." />}>
                  <ThermostatDashboard
                    key={selected.devEui}
                    currentUser={currentUser}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "leak" && (
                <Suspense fallback={<LoadingSpinner message="Loading leak dashboard..." />}>
                  <LeakDashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "air" && (
                <Suspense fallback={<LoadingSpinner message="Loading air dashboard..." />}>
                  <AirDashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "tbhh100" && (
                <Suspense fallback={<LoadingSpinner message="Loading healthy home sensor dashboard..." />}>
                  <Tbhh100Dashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "ember" && (
                <Suspense fallback={<LoadingSpinner message="Loading Ember dashboard..." />}>
                  <EmberDashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "controller" && (
                <Suspense fallback={<LoadingSpinner message="Loading controller dashboard..." />}>
                  <ControllerDashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                  />
                </Suspense>
              )}
              {selected?.type === "vs330" && (
                <Suspense fallback={<LoadingSpinner message="Loading occupancy dashboard..." />}>
                  <OccupancyDashboard
                    key={selected.devEui}
                    devEui={selected.devEui}
                    device={selected}
                    buildings={buildings}
                    currentUser={currentUser}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
              {selected?.type === "dragino" && (
                <Suspense fallback={<LoadingSpinner message="Loading Dragino dashboard..." />}>
                  <DraginoDashboard
                    key={`${selected.devEui}:${selectedDraginoCardRole || "default"}`}
                    dashboardRoleLevel={currentUser?.dashboardRoleLevel ?? 1}
                    currentUser={currentUser}
                    devEui={selected.devEui}
                    device={selected}
                    cardRole={selectedDraginoCardRole}
                    buildings={buildings}
                    showToast={showToast}
                    onLocationSaved={handleDeviceLocationSaved}
                    onFullscreenChange={setIsFullscreenOpen}
                  />
                </Suspense>
              )}
            </>
          )}

          {tab === "logs" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading admin..." />}>
                <LogsDashboard showToast={showToast} currentUser={currentUser} />
              </Suspense>
            </div>
          )}

          {tab === "alarms" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading alarm setup..." />}>
                <AlarmSetup showToast={showToast} currentUser={currentUser} />
              </Suspense>
            </div>
          )}

          {tab === "sitings" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading sites..." />}>
                <SitingsPage
                  buildings={buildings}
                  devices={devices}
                  selectedBuildingId={selectedBuildingId}
                  currentUser={currentUser}
                  showToast={showToast}
                  onRefreshBuildings={async () => {
                    const buildingList = await fetchBuildings().catch(() => null);
                    if (Array.isArray(buildingList) && buildingList.length > 0) {
                      setBuildings(buildingList.map(normalizeBuildingRecord));
                    }
                  }}
                  onUpdateBuildingLocation={(updatedBuilding) => {
                    setBuildings((prev) =>
                      prev.map((building) =>
                        getBuildingRecordId(building) === getBuildingRecordId(updatedBuilding)
                          ? { ...building, ...updatedBuilding }
                          : building
                      )
                    );
                  }}
                />
              </Suspense>
            </div>
          )}

          {tab === "users" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                <UserManagement currentUser={currentUser} showToast={showToast} />
              </Suspense>
            </div>
          )}

          {tab === "sensor-categories" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading categories..." />}>
                <SensorCategoriesPage currentUser={currentUser} showToast={showToast} />
              </Suspense>
            </div>
          )}

          {tab === "thermostat-groups" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading thermostat groups..." />}>
                <ThermostatGroupsPage currentUser={currentUser} showToast={showToast} />
              </Suspense>
            </div>
          )}

          {tab === "controller-io" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading controller I/O..." />}>
                <ControllerIoPage currentUser={currentUser} showToast={showToast} />
              </Suspense>
            </div>
          )}

          {tab === "automations" && (
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <Suspense fallback={<LoadingSpinner message="Loading automations..." />}>
                <AutomationsPage currentUser={currentUser} showToast={showToast} />
              </Suspense>
            </div>
          )}
        </DashboardShell>
      ) : authError ? (
        <div
          className="auth-error-screen"
          style={{
            backgroundColor: "var(--panel-a, #f8fafc)",
          }}
        >
          <div
            className="auth-error-card"
            style={{
              maxWidth: "600px",
              padding: "32px",
              backgroundColor: "var(--panel, #ffffff)",
              borderRadius: "12px",
              border: "2px solid var(--red-300, #fca5a5)",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ 
              fontSize: "48px", 
              textAlign: "center", 
              marginBottom: "16px" 
            }}>⚠️</div>
            <h2 style={{ 
              margin: "0 0 16px 0", 
              color: "var(--ink-900, #0f172a)",
              fontSize: "24px",
              textAlign: "center"
            }}>{authError.message}</h2>
            <p style={{ 
              margin: "0 0 24px 0", 
              color: "var(--ink-600, #475569)",
              lineHeight: "1.6",
              whiteSpace: "pre-line",
            }}>{authError.details}</p>
            {authError.invalidSession401 ? (
              <div style={{
                padding: "16px",
                backgroundColor: "var(--panel-a, #f8fafc)",
                borderRadius: "8px",
                marginBottom: "24px",
                fontSize: "13px",
                color: "var(--ink-700, #334155)"
              }}>
                <strong>What this means:</strong>
                <p style={{ margin: "8px 0 0 0" }}>
                  Central IoT calls your <strong>auth service</strong> to verify the token for service <code>iot</code>. A 401 there means that check failed (revoked session, wrong/missing IoT role, or auth URL/key mismatch in Docker).
                </p>
                {authError.clearedRejectedToken ? (
                  <p style={{ margin: "8px 0 0 0" }}>
                    The rejected token was <strong>removed</strong> from this browser. <strong>Try Again</strong> only reloads; use <strong>Sign in again</strong> to go back to central auth.
                  </p>
                ) : (
                  <p style={{ margin: "8px 0 0 0" }}>
                    <strong>Debug mode</strong> kept the bad token so you can inspect it. <strong>Sign in again</strong> or <strong>Go to Central Auth</strong> clears it first, then opens login.
                  </p>
                )}
              </div>
            ) : null}
            {authError.debugStuckNoToken ? (
              <div style={{
                padding: "16px",
                backgroundColor: "var(--panel-a, #f8fafc)",
                borderRadius: "8px",
                marginBottom: "24px",
                fontSize: "13px",
                color: "var(--ink-700, #334155)"
              }}>
                <strong>Quick fixes:</strong>
                <ol style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  <li>
                    <strong>Sign in:</strong> use <strong>Go to Central Auth</strong> below (keeps debug if you stay on this URL without a token).
                  </li>
                  <li>
                    <strong>Normal auto-redirect login:</strong> click <strong>Exit debug mode &amp; reload</strong> — then the app sends you to central auth automatically if still no token.
                  </li>
                </ol>
              </div>
            ) : !authError.invalidSession401 ? (
              <div style={{
                padding: "16px",
                backgroundColor: "var(--panel-a, #f8fafc)",
                borderRadius: "8px",
                marginBottom: "24px",
                fontSize: "13px",
                color: "var(--ink-700, #334155)"
              }}>
                <strong>To fix this:</strong>
                <ol style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  <li>Open browser console (F12 or Cmd+Option+I) and check for errors</li>
                  <li>Look for <code>429 Too Many Requests</code> errors from central auth</li>
                  <li>Try logging out and logging back in from the central auth page</li>
                  <li>Wait a few minutes if rate limiting is occurring, then try again</li>
                  <li>Check that the central auth system is configured to pass a token in the redirect URL</li>
                </ol>
              </div>
            ) : null}
            {authError.error && (
              <div style={{
                padding: "12px",
                backgroundColor: "var(--panel-a, #f8fafc)",
                borderRadius: "6px",
                marginBottom: "16px",
                fontSize: "12px",
                fontFamily: "monospace",
                color: "var(--ink-700, #334155)",
                maxHeight: "200px",
                overflow: "auto",
                border: "1px solid var(--ink-200, #e2e8f0)"
              }}>
                <strong>Error Details:</strong>
                <pre style={{ margin: "8px 0 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {JSON.stringify(authError.error, null, 2)}
                </pre>
              </div>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              {authError.invalidSession401 ? (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => clearStoredSessionAndOpenCentralAuth()}
                >
                  Sign in again
                </button>
              ) : null}
              <button
                type="button"
                className={authError.invalidSession401 ? "btn ghost" : "btn primary"}
                onClick={() => {
                  const isRedirectLoop = authError.message === "Could not complete sign-in";
                  if (!isRedirectLoop) {
                    sessionStorage.removeItem("authRedirectAttempt");
                    sessionStorage.removeItem("authRedirectCount");
                  }
                  if (isRedirectLoop) {
                    try {
                      sessionStorage.setItem("iotAuthPauseRedirect", "true");
                    } catch {
                      /* ignore */
                    }
                  }
                  if (authError.invalidSession401) {
                    try {
                      localStorage.removeItem("authToken");
                      localStorage.removeItem("user");
                    } catch {
                      /* ignore */
                    }
                  }
                  setAuthError(null);
                  window.location.reload();
                }}
              >
                {authError.invalidSession401
                  ? "Clear saved token & reload"
                  : authError.message === "Could not complete sign-in"
                    ? "Retry without auto-redirect"
                    : "Try Again"}
              </button>
              {authError.debugStuckNoToken ? (
                <button
                  className="btn primary"
                  type="button"
                  onClick={() => {
                    try {
                      localStorage.removeItem("authDebug");
                      sessionStorage.removeItem("iotAuthPauseRedirect");
                      const url = new URL(window.location.href);
                      url.searchParams.delete("debug");
                      sessionStorage.removeItem("authRedirectAttempt");
                      sessionStorage.removeItem("authRedirectCount");
                      window.location.href = url.pathname + url.search + url.hash;
                    } catch {
                      window.location.reload();
                    }
                  }}
                >
                  Exit debug mode &amp; reload
                </button>
              ) : (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    localStorage.setItem("authDebug", "true");
                    const url = new URL(window.location.href);
                    url.searchParams.set("debug", "true");
                    window.location.href = url.toString();
                  }}
                >
                  Enable Debug Mode
                </button>
              )}
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  if (authError.invalidSession401) {
                    clearStoredSessionAndOpenCentralAuth();
                    return;
                  }
                  try {
                    localStorage.removeItem("authDebug");
                  } catch {
                    /* ignore */
                  }
                  sessionStorage.removeItem("iotAuthPauseRedirect");
                  sessionStorage.removeItem("authRedirectAttempt");
                  sessionStorage.removeItem("authRedirectCount");
                  try {
                    logUiEvent("Auth", "Opening Central Auth (leave IoT dashboard)");
                  } catch {
                    /* ignore */
                  }
                  window.location.assign(buildCentralAuthUrl());
                }}
              >
                Go to Central Auth
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="app" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
          <LoadingSpinner message="Waiting for authentication token..." />
        </div>
      )}
    </ThemeProvider>
  );
}
